import { useApp } from '../state/AppContext.js';
import { Icon } from '../icons/Icon.js';
import { Card } from '../components/Card.js';
import { Button } from '../components/Button.js';
import { LineTag } from '../components/Tag.js';
import type { RouteKey } from '../state/router.js';
import type { IconName } from '../icons/registry.js';

type Entry = {
  key: RouteKey;
  title: string;
  desc: string;
  icon: IconName;
  tag: string;
  wip?: boolean;
};

const ENTRIES: Entry[] = [
  { key: 'object', title: '物体替换', icon: 'object', tag: '替换 · 可用',
    desc: '面向商品、主体、道具替换的批量生产流程，支持 Prompt 与策略调参。' },
  { key: 'scene', title: '场景替换', icon: 'image', tag: '替换 · 可用',
    desc: '保持主体一致，将视频背景或场景批量改造。' },
  { key: 'person', title: '人物替换', icon: 'user', tag: '替换 · 可用',
    desc: '面向真人形象、服饰和身份特征的批量替换。' },
  { key: 'viral', title: '爆款拆解', icon: 'telescope', tag: '创作 · 开发中', wip: true,
    desc: '把参考视频拆成卖点结构、节奏和可复用素材线索。' },
  { key: 'storyboard', title: '分镜脚本', icon: 'grid', tag: '创作 · 开发中', wip: true,
    desc: '将脚本整理成场级 Prompt，为后续生成做准备。' },
  { key: 'video', title: '视频生成', icon: 'video', tag: '创作 · 开发中', wip: true,
    desc: '按场管理生成版本、重生成和已选结果。' },
];

const STEPS = ['配置存储与模型', '上传源视频 + 参考素材', '写 Prompt + 选替换策略', '提交执行，去任务管理看结果'];

export function Experience() {
  const { actions } = useApp();
  return (
    <>
      <Card>
        <div className="hero">
          <h1 className="hero-title">AI 视频创作 Studio</h1>
          <p className="hero-desc">
            从一个入口进入「替换」与「创作」两条主链路。当前 P0 上线「替换」三件套，「创作」开发中。
            上传素材 → 写 Prompt + 选策略 → 提交，几分钟内拿到批量变体。
          </p>
          <div className="hero-steps">
            {STEPS.map((s, i) => (
              <div className="hero-step" key={i}>
                <span className="hero-step-n">{i + 1}</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div className="row" style={{ marginTop: 'var(--space-m)' }}>
            <Button variant="primary" leftIcon={<Icon name="object" size={16} />}
              onClick={() => actions.navigate('object')}>开始替换</Button>
            <Button variant="ghost" rightIcon={<Icon name="arrowRight" size={16} />}
              onClick={() => actions.navigate('history')}>进入任务管理</Button>
          </div>
        </div>
      </Card>

      <div className="entry-grid">
        {ENTRIES.map((e) => (
          <div
            key={e.key}
            className={'entry-card' + (e.wip ? ' wip' : '')}
            role="button"
            tabIndex={0}
            onClick={() => actions.navigate(e.key)}
            onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') actions.navigate(e.key); }}
          >
            <div className="entry-top">
              <div className="entry-icon"><Icon name={e.icon} size={20} /></div>
              <LineTag>{e.tag}</LineTag>
            </div>
            <div className="entry-title">{e.title}</div>
            <div className="entry-desc">{e.desc}</div>
            <div className="entry-cta">
              {e.wip ? '即将上线' : '进入'}
              <Icon name="arrowRight" size={14} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
